import {
  VideoRecorder,
  VideoRecorderProps,
  VideoRecording,
} from "@amzn/itsvideo-recorder";
import { RefObject, useContext } from "react";

import { Logger } from "@/apis/Logger";
import { Metrics } from "@/apis/Metrics";
import SubmitVideoButton from "@/components/Commons/SubmitVideoButton";
import { RefPageValue } from "@/components/GetSupport/ChatWithITSupport/types";
import { SnowplowTracker } from "@/helpers/snowplow";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventCategory } from "@/types/snowplow-events";

import RecorderError from "./RecorderError";

type VideoRecorderCustomProps = Omit<
  VideoRecorderProps,
  "onRecord" | "onRetry" | "children" | "retryI18nId"
> & {
  /*** The Snowplow event category ***/
  eventCategory: Day1EventCategory;

  /**
   * For full remote ID verification only (FRIV):
   * Callback used to navigate the client back to the main ID verification steps on clicking
   * the button to submit for review at the end of each record step
   */
  clearSelectedStep?: () => void;

  /**
   * The reference to the FullIdVerification container element used to calculate the window
   * coordinate to scroll back to after the client completes ID recording steps.
   */
  fullIdVerificationElementRef?: RefObject<HTMLDivElement>;

  /**
   * Who mounted this component? Used for metrics collection on sub components.
   */
  refPage?: RefPageValue;
};

const VIDEO_RECORDER_ERROR_PUBLISHER =
  Metrics.publisherInstance.newChildActionPublisherForMethod(
    "ManualIdvVideoRecorder"
  );

export default function VideoRecorderCustom(
  props: VideoRecorderCustomProps
): JSX.Element {
  const { dispatch } = useContext(AppContext);
  const retryRecording = () => dispatch({ type: ActionType.POP_DOCUMENT });
  const saveRecording = (videoRecording: VideoRecording) =>
    dispatch({
      type: ActionType.ADD_DOCUMENT,
      document: { ...videoRecording, submitClicked: false },
    });

  /**
   * Emit error metrics and error logs generated from the react-video-recorder.
   * @param error An Error instance
   */
  /* istanbul ignore next reason: Video recording cannot be tested currently */
  const handleError = (error: Error) => {
    void Logger.fatal(
      "Unable to complete video recording due to some errors.",
      error
    );
    VIDEO_RECORDER_ERROR_PUBLISHER.publishCounterMonitor(
      "VideoRecorderError",
      1
    );
  };

  return (
    <div data-test-id="videoRecorderDataTestId">
      <VideoRecorder
        retryI18nId={"identityCheck-button-retry"}
        onRecord={saveRecording}
        onRetry={retryRecording}
        snowplowTracker={SnowplowTracker.instance}
        onError={handleError}
        onErrorRenderer={(error: Error) => <RecorderError error={error} />}
        {...props}
      >
        <SubmitVideoButton
          eventCategory={props.eventCategory}
          clearSelectedStep={props.clearSelectedStep}
          fullIdVerificationElementRef={props.fullIdVerificationElementRef}
          refPage={props.refPage}
        />
      </VideoRecorder>
    </div>
  );
}
